import {
  Button,
  Chip,
  Dialog,
  DatePickerDropdown,
  MenuItem,
  Select,
  Text,
  TextBadge,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { getHasTierGraphics } from '@/pages/CustomerDashboard/helpers';
import { TierImage } from '@/pages/CustomerDashboard/TierImage';
import { getRankCurrent, getTier } from '@/pages/FarmerList/helper';
import { getHistoryEntryRank, getTierHistoryBadgeText } from '@/pages/FarmerList/TierStatusCard';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { ApiFarmerRewards, ApiRewardsProgram, ApiUser } from '@api/interfaces';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, useTheme,
} from '@mui/material';
import { businessToday, formatLocalDate, formatMediumDate } from '@shared/utilities';
import { getAccountName } from '@shared/utilities/UserUtilities';
import { sortBy } from 'lodash';
import i18next from 'i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

interface TierOverrideModalProps {
  customer: ApiUser,
  farmerReward?: ApiFarmerRewards,
  isJobProcessing?: boolean,
  rewardsProgram: ApiRewardsProgram,
  setShow: Dispatch<SetStateAction<boolean>>,
  show: boolean,
}

const getInitials = (name: string) => (
  name.split(' ').filter((_, i, a) => i === 0 || i === a.length - 1).map((p) => p[0]).join('')
);

export const TierOverrideModal = ({
  customer,
  farmerReward,
  isJobProcessing,
  rewardsProgram,
  setShow,
  show,
}: TierOverrideModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [selectedTierId, setSelectedTierId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<Date | undefined>(new Date());
  const [genericError, setGenericError] = useState('');
  const [dateError, setDateError] = useState('');
  const [tierError, setTierError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successEffectiveDate, setSuccessEffectiveDate] = useState('');

  const tiers = sortBy(rewardsProgram.tiers ?? [], 'rank');
  const hasTierGraphics = getHasTierGraphics(tiers.map((tier) => tier.name));
  const tierHistory = farmerReward?.tierHistory ?? [];
  const farmerRankCurrent = getRankCurrent(farmerReward, rewardsProgram);
  const currentTier = farmerRankCurrent !== null ? getTier(farmerRankCurrent, tiers) : undefined;
  const activeTier = tierHistory.find((entry) => !entry.endDate);
  const todayStr = businessToday();

  const accountName = getAccountName(customer);

  const onClose = () => {
    setShow(false);
    setSelectedTierId('');
    setEffectiveDate(new Date());
    setDateError('');
    setGenericError('');
    setTierError('');
    setSuccess(false);
    setSuccessEffectiveDate('');
  };

  const handleTierChange = (tierId: string) => {
    setSelectedTierId(tierId);
    setTierError('');
  };

  const handleEffectiveDateChange = (date: Date | undefined) => {
    setEffectiveDate(date);
    setDateError('');
  };

  const handleSubmit = async () => {
    setDateError('');
    setGenericError('');
    setTierError('');
    const selectedTier = tiers.find((tier) => tier.id === selectedTierId);
    if (!selectedTier) {
      setTierError(t($ => $.customers.details.tierOverride.errors.tierRequired));
      return;
    }
    if (!effectiveDate) {
      return;
    }
    const effectiveDateStr = formatLocalDate(effectiveDate);

    if (farmerRankCurrent !== null && selectedTier.rank < farmerRankCurrent) {
      setTierError(t($ => $.customers.details.tierOverride.errors.tierTooLow, {
        tierLabel: currentTier?.name,
      }));
      return;
    }
    if (effectiveDateStr > todayStr) {
      setDateError(t($ => $.customers.details.tierOverride.errors.futureDate));
      return;
    }

    if (!customer.externalId) {
      setGenericError(t($ => $.customers.details.tierOverride.errors.missingExternalId));
      return;
    }

    setSubmitting(true);
    try {
      await RewardsProgramApi.saveTierOverride(rewardsProgram.id, [{
        effectiveDate: effectiveDateStr,
        externalId: customer.externalId,
        rank: selectedTier.rank,
      }]);
      void queryClient.invalidateQueries([QueryKeys.GET_FARMER_REWARDS, customer.id]);
      void queryClient.invalidateQueries(QueryKeys.GET_JOB_IS_PROCESSING);
      setSuccess(true);
      setSuccessEffectiveDate(effectiveDateStr);
    } catch (e) {
      setGenericError((e as Error).message ?? t($ => $.common.error.genericError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      acceptButton={(props) => (success ? null : (
        <Tooltip
          title={isJobProcessing
            ? t($ => $.customers.details.tierOverride.reprocessingTooltip)
            : ''}
        >
          <span>
            <Button
              {...props}
              disabled={!effectiveDate}
              loading={submitting || isJobProcessing}
              onClick={handleSubmit}
              testID="tier-override-save-button"
              variant="contained"
            >
              {t($ => $.customers.details.tierOverride.setOverride)}
            </Button>
          </span>
        </Tooltip>
      ))}
      cancelButton={(props) => (
        <Button {...props} color="inherit" onClick={onClose} testID="tier-override-cancel-button">
          {success ? t($ => $.common.actions.close) : t($ => $.common.actions.cancel)}
        </Button>
      )}
      maxWidth="sm"
      onClose={onClose}
      open={show}
      showCloseButton={false}
      testID="tier-override-modal"
      title={t($ => $.customers.details.tierOverride.modalTitle)}
    >
      <Box sx={{
        alignItems: 'center',
        bgcolor: theme.palette.surface2,
        borderRadius: 2,
        display: 'flex',
        gap: '12px',
        mb: '20px',
        p: '12px 16px',
      }}
      >
        <Box sx={{
          alignItems: 'center',
          bgcolor: theme.palette.primaryContainer,
          borderRadius: '50%',
          color: 'primary.main',
          display: 'flex',
          fontWeight: 500,
          height: '40px',
          justifyContent: 'center',
          width: '40px',
        }}
        >
          {getInitials(accountName)}
        </Box>
        <Text category="title-small" sx={{ flex: 1 }}>{accountName}</Text>
        <Box sx={{ textAlign: 'right' }}>
          <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
            {t($ => $.customers.details.erpId)}
          </Text>
          <Text category="title-small">{customer.externalId || '-'}</Text>
        </Box>
      </Box>

      <Box sx={{
        alignItems: 'center',
        bgcolor: theme.palette.surface2,
        border: `1px solid ${theme.palette.outline}`,
        borderRadius: 2,
        display: 'flex',
        mb: '20px',
        p: '14px 16px',
      }}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: '10px' }}>
          {!!currentTier && (
            <TierImage
              hasTierGraphics={hasTierGraphics}
              size={{
                height: '30px',
                width: 'auto',
              }}
              tier={currentTier}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
              {t($ => $.customers.details.tierOverride.currentTierLabel)}
            </Text>
            <Text category="title-medium">
              {currentTier ? currentTier.name : t($ => $.customers.details.notEnrolled)}
            </Text>
          </Box>
        </Box>
        {!!activeTier && (
          <Box sx={{ flex: 1 }}>
            <Text category="overline" sx={{ color: theme.palette.text.secondary }}>
              {t($ => $.customers.details.tierOverride.asOf)}
            </Text>
            <Text category="title-medium">
              {formatMediumDate(activeTier.effectiveDate, i18next)}
            </Text>
          </Box>
        )}
        {!!activeTier && (
          <TextBadge
            backgroundColor={theme.palette.alert.warning.background}
            text={getTierHistoryBadgeText(activeTier.type, t)}
            textCategory="overline"
            textColor="#8a5a00"
          />
        )}
      </Box>

      {success && (
        <Box sx={{
          alignItems: 'center',
          bgcolor: theme.palette.alert.success.background,
          borderRadius: 2,
          display: 'flex',
          gap: '10px',
          mb: '16px',
          p: '12px 16px',
        }}
        >
          <CheckCircleIcon sx={{ color: theme.palette.success.main, flexShrink: 0 }} />
          <Text category="body-medium" sx={{ color: '#237804' }}>
            {t($ => $.customers.details.tierOverride.successMessage, {
              effectiveDate: formatMediumDate(successEffectiveDate, i18next),
            })}
          </Text>
        </Box>
      )}

      {tierHistory.length > 0 && (
        <Box sx={{ mb: '20px' }}>
          <Text category="title-small">
            {t($ => $.customers.details.tierOverride.historyTitle)}
          </Text>
          <VSpacer size="2" />
          <Box sx={{
            border: `1px solid ${theme.palette.outline}`, borderRadius: 2, overflow: 'hidden',
          }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <TableCell sx={{ borderBottom: `1px solid ${theme.palette.outline}`, p: '10px 12px' }}>
                    <Text
                      category="overline"
                      sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                    >
                      {t($ => $.customers.details.tierOverride.tableTier)}
                    </Text>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${theme.palette.outline}`, p: '10px 12px' }}>
                    <Text
                      category="overline"
                      sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                    >
                      {t($ => $.customers.details.tierOverride.tableEffectiveDate)}
                    </Text>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${theme.palette.outline}`, p: '10px 12px' }}>
                    <Text
                      category="overline"
                      sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                    >
                      {t($ => $.customers.details.tierOverride.tableEndDate)}
                    </Text>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${theme.palette.outline}`, p: '10px 12px' }}>
                    <Text
                      category="overline"
                      sx={{ color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}
                    >
                      {t($ => $.customers.details.tierOverride.tableCreatedAt)}
                    </Text>
                  </TableCell>
                  <TableCell sx={{ borderBottom: `1px solid ${theme.palette.outline}`, p: '10px 12px' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {[...tierHistory].reverse().map((entry, index, reversedHistory) => {
                  const tier = getTier(getHistoryEntryRank(entry) ?? -1, tiers);
                  const isLastRow = index === reversedHistory.length - 1;
                  const cellSx = {
                    borderBottom: isLastRow ? 'none' : '1px solid rgba(0,0,0,0.06)',
                    p: '10px 12px',
                  };
                  return (
                    <TableRow key={entry.id}>
                      <TableCell sx={cellSx}>
                        {!!tier && (
                          <Chip
                            color="primary"
                            label={tier.name}
                            rounded
                            size="small"
                            sx={{ textTransform: 'uppercase' }}
                            testID={`tier-override-history-tier-${entry.id}`}
                            variant="filled"
                          />
                        )}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        {formatMediumDate(entry.effectiveDate, i18next)}
                      </TableCell>
                      <TableCell sx={{
                        ...cellSx,
                        color: entry.endDate ? undefined : theme.palette.text.secondary,
                      }}
                      >
                        {entry.endDate
                          ? formatMediumDate(entry.endDate, i18next)
                          : t($ => $.customers.details.tierOverride.present)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: theme.palette.text.secondary }}>
                        {formatMediumDate(entry.createdAt, i18next)}
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <TextBadge
                          backgroundColor={theme.palette.alert.warning.background}
                          sx={{ width: 'fit-content' }}
                          text={getTierHistoryBadgeText(entry.type, t)}
                          textCategory="overline"
                          textColor="#8a5a00"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <VSpacer size="5" />
        </Box>
      )}
      {!!genericError && (
        <>
          <Text category="body-small" sx={{ color: theme.palette.error.main }}>
            {genericError}
          </Text>
          <VSpacer size="3" />
        </>
      )}
      {!success && (
        <>
          <Select
            error={!!tierError}
            label={t($ => $.customers.details.tierOverride.tierFieldLabel)}
            onChangeValue={handleTierChange}
            testID="tier-override-tier-select"
            value={selectedTierId}
          >
            {tiers.map((tier) => (
              <MenuItem
                disabled={farmerRankCurrent !== null && tier.rank < farmerRankCurrent}
                key={tier.id}
                testID={`tier-override-tier-option-${tier.id}`}
                value={tier.id}
              >
                {tier.name}
              </MenuItem>
            ))}
          </Select>
          {!!tierError && (
            <>
              <VSpacer size="3" />
              <Text category="body-small" sx={{ color: theme.palette.error.main }}>
                {tierError}
              </Text>
            </>
          )}
          <VSpacer size="5" />
          <DatePickerDropdown
            errorMessage={dateError}
            label={t($ => $.customers.details.tierOverride.dateFieldLabel)}
            onChange={handleEffectiveDateChange}
            testID="tier-override-date-input"
            value={effectiveDate}
          />
        </>
      )}
    </Dialog>
  );
};
